import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Car, Plus, Trash2, MapPin, TrendingUp, Bookmark, FileBarChart, Printer, Download, FileText, Flag, Pencil, Clock, Check, Navigation, Square, X } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Modal from "@/components/Modal";
import RouteMap from "@/components/RouteMap";

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const PERIODS = [
  { v: "day", l: "Day" },
  { v: "week", l: "Week" },
  { v: "month", l: "Month" },
  { v: "year", l: "Year" },
];

const computeMiles = (s, e) => {
  const a = parseFloat(s), b = parseFloat(e);
  if (isNaN(a) || isNaN(b)) return null;
  return Math.round((b - a) * 10) / 10;
};

export default function CashMiles() {
  const today = new Date().toISOString().slice(0, 10);
  const { user, refreshUser } = useAuth();
  const presets = user?.mile_presets || [];
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState({ entries: [], total_miles: 0, total_deduction: 0, rate: 0.72 });
  const [form, setForm] = useState({ start_location: "", end_location: "", start_mile: "", end_mile: "", date: today });
  const [busy, setBusy] = useState(false);
  const [lastOdo, setLastOdo] = useState(null);
  const [openTrips, setOpenTrips] = useState([]);
  const [endEdits, setEndEdits] = useState({});
  const [mapRoute, setMapRoute] = useState(null);
  const [mapLoading, setMapLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const geoCache = useRef({});

  const geocode = async (q) => {
    if (!q || !q.trim()) return null;
    const key = q.trim().toLowerCase();
    if (geoCache.current[key]) return geoCache.current[key];
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(q)}`, { headers: { Accept: "application/json" } });
      const arr = await res.json();
      if (!arr || !arr.length) return null;
      const p = { lat: parseFloat(arr[0].lat), lon: parseFloat(arr[0].lon), label: q };
      geoCache.current[key] = p;
      return p;
    } catch { return null; }
  };
  const routeLine = async (a, b) => {
    try {
      const res = await fetch(`https://router.project-osrm.org/route/v1/driving/${a.lon},${a.lat};${b.lon},${b.lat}?overview=full&geometries=geojson`);
      const j = await res.json();
      const coords = j?.routes?.[0]?.geometry?.coordinates;
      if (coords && coords.length) return coords.map((c) => [c[1], c[0]]);
    } catch { /* fall back to straight line */ }
    return null;
  };
  const buildRoute = async (startLoc, endLoc) => {
    if ((!startLoc || !startLoc.trim()) && (!endLoc || !endLoc.trim())) { setMapRoute(null); return; }
    setMapLoading(true);
    try {
      const [s, e] = await Promise.all([geocode(startLoc), geocode(endLoc)]);
      if (!s && !e) { setMapRoute(null); toast.error("Couldn't find those locations on the map"); return; }
      let line = null;
      if (s && e) line = await routeLine(s, e);
      setMapRoute({ start: s, end: e, line });
    } finally { setMapLoading(false); }
  };
  const selectRoute = (e) => { setSelectedId(e.id); buildRoute(e.start_location, e.end_location); };

  // ---- Live GPS tracking ----
  const [tracking, setTracking] = useState(false);
  const [livePath, setLivePath] = useState([]);
  const [liveCurrent, setLiveCurrent] = useState(null);
  const [liveStats, setLiveStats] = useState({ miles: 0 });
  const [liveMiles, setLiveMiles] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const watchRef = useRef(null);
  const timerRef = useRef(null);
  const pathRef = useRef([]);
  const startTimeRef = useRef(null);

  const haversine = (a, b) => {
    const R = 3958.8; // miles
    const dLat = (b[0] - a[0]) * Math.PI / 180, dLon = (b[1] - a[1]) * Math.PI / 180;
    const la1 = a[0] * Math.PI / 180, la2 = b[0] * Math.PI / 180;
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLon / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(h));
  };
  const pathMiles = (pts) => pts.reduce((s, _, i) => (i ? s + haversine(pts[i - 1], pts[i]) : 0), 0);
  const reverseGeocode = async (pt) => {
    try {
      const r = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&zoom=16&lat=${pt[0]}&lon=${pt[1]}`);
      const j = await r.json();
      if (j?.name) return j.name;
      if (j?.display_name) return j.display_name.split(",").slice(0, 2).join(",").trim();
    } catch { /* ignore */ }
    return "";
  };
  const startTracking = () => {
    if (!("geolocation" in navigator)) return toast.error("GPS is not available on this device");
    pathRef.current = [];
    setLivePath([]); setLiveCurrent(null); setLiveStats({ miles: 0 });
    setLiveMiles(null); setMapRoute(null); setSelectedId(null); setElapsed(0);
    startTimeRef.current = Date.now();
    setTracking(true);
    timerRef.current = setInterval(() => setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000)), 1000);
    watchRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const pt = [pos.coords.latitude, pos.coords.longitude];
        const last = pathRef.current[pathRef.current.length - 1];
        if (last && haversine(last, pt) < 0.0009) { setLiveCurrent(pt); return; }
        pathRef.current = [...pathRef.current, pt];
        setLivePath(pathRef.current);
        setLiveCurrent(pt);
        setLiveStats({ miles: Math.round(pathMiles(pathRef.current) * 10) / 10 });
      },
      (err) => { toast.error(err.code === 1 ? "Location permission denied — enable GPS to track" : "Couldn't get a GPS signal"); stopTracking(false); },
      { enableHighAccuracy: true, maximumAge: 1000, timeout: 20000 }
    );
    toast.success("Live tracking started — drive safe!");
  };
  const stopTracking = async (finish = true) => {
    if (watchRef.current != null) { navigator.geolocation.clearWatch(watchRef.current); watchRef.current = null; }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setTracking(false);
    const path = pathRef.current;
    if (!finish) { setLivePath([]); setLiveCurrent(null); return; }
    if (path.length < 2) { toast.error("Not enough movement was recorded to log a trip"); setLivePath([]); setLiveCurrent(null); return; }
    const m = Math.round(pathMiles(path) * 10) / 10;
    setLiveMiles(m);
    setMapRoute({ start: { lat: path[0][0], lon: path[0][1], label: "Start" }, end: { lat: path[path.length - 1][0], lon: path[path.length - 1][1], label: "End" }, line: path });
    setLivePath([]); setLiveCurrent(null);
    toast.success(`Trip recorded: ${m} mi`);
    const [s, e] = await Promise.all([reverseGeocode(path[0]), reverseGeocode(path[path.length - 1])]);
    setForm((f) => ({ ...f, start_location: f.start_location || s, end_location: f.end_location || e }));
  };
  useEffect(() => () => {
    if (watchRef.current != null) navigator.geolocation.clearWatch(watchRef.current);
    if (timerRef.current) clearInterval(timerRef.current);
  }, []);
  const fmtDur = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  const load = useCallback(() => {
    api.get(`/cash-miles?period=${period}&ref_date=${today}`).then((r) => setData(r.data)).catch(() => {});
    api.get(`/cash-miles/open`).then((r) => setOpenTrips(r.data.entries || [])).catch(() => {});
    api.get(`/cash-miles?period=year&ref_date=${today}`).then((r) => {
      const withOdo = (r.data.entries || []).filter((e) => e.end_mile != null);
      setLastOdo(withOdo.length ? withOdo[0].end_mile : null);
    }).catch(() => {});
  }, [period, today]);
  useEffect(() => { load(); }, [load]);

  const hasStart = form.start_mile !== "" && !isNaN(parseFloat(form.start_mile));
  const hasEnd = form.end_mile !== "" && !isNaN(parseFloat(form.end_mile));
  const miles = hasEnd ? computeMiles(form.start_mile, form.end_mile) : null;
  const invalidRange = miles != null && miles < 0;

  const submit = async () => {
    if (liveMiles != null) {
      if (!form.date) return toast.error("Pick a date");
      setBusy(true);
      try {
        await api.post("/cash-miles", { start_location: form.start_location, end_location: form.end_location, miles: liveMiles, date: form.date });
        toast.success("Trip logged");
        const sLoc = form.start_location, eLoc = form.end_location;
        setForm({ start_location: "", end_location: "", start_mile: "", end_mile: "", date: today });
        setLiveMiles(null);
        load();
        if (sLoc || eLoc) buildRoute(sLoc, eLoc);
      } catch (err) { toast.error(err?.response?.data?.detail || "Could not save trip"); }
      finally { setBusy(false); }
      return;
    }
    if (!hasStart) return toast.error("Enter starting odometer miles");
    if (!form.date) return toast.error("Pick a date");
    const s = parseFloat(form.start_mile);
    setBusy(true);
    try {
      if (hasEnd) {
        const e = parseFloat(form.end_mile);
        if (e < s) { setBusy(false); return toast.error("Ending miles must be greater than starting miles"); }
        await api.post("/cash-miles", { start_location: form.start_location, end_location: form.end_location, start_mile: s, end_mile: e, date: form.date });
        toast.success("Trip logged");
      } else {
        await api.post("/cash-miles", { start_location: form.start_location, end_location: form.end_location, start_mile: s, date: form.date });
        toast.success("Trip started — finish it when you're back");
      }
      const sLoc = form.start_location, eLoc = form.end_location;
      setForm({ start_location: "", end_location: "", start_mile: "", end_mile: "", date: today });
      load();
      if (sLoc || eLoc) buildRoute(sLoc, eLoc);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not save trip"); }
    finally { setBusy(false); }
  };
  const useLastOdo = () => setForm((f) => ({ ...f, start_mile: String(lastOdo) }));
  const quickStart = async () => {
    if (lastOdo == null) return;
    setBusy(true);
    try {
      await api.post("/cash-miles", { start_mile: lastOdo, date: today });
      toast.success(`Trip started at ${lastOdo} mi — finish it when you're back`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not start trip"); }
    finally { setBusy(false); }
  };
  const del = async (id) => { await api.delete(`/cash-miles/${id}`); load(); };

  const finishTrip = async (t) => {
    const raw = endEdits[t.id];
    const e = parseFloat(raw);
    if (isNaN(e)) return toast.error("Enter ending odometer miles");
    if (t.start_mile != null && e < t.start_mile) return toast.error("Ending miles must be greater than starting miles");
    try {
      const body = { end_mile: e };
      if (endEdits[`${t.id}-loc`] != null) body.end_location = endEdits[`${t.id}-loc`];
      await api.patch(`/cash-miles/${t.id}`, body);
      toast.success("Trip finished");
      setEndEdits((m) => { const n = { ...m }; delete n[t.id]; delete n[`${t.id}-loc`]; return n; });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not finish trip"); }
  };

  // Edit existing trip
  const [edit, setEdit] = useState(null);
  const saveEdit = async () => {
    const s = parseFloat(edit.start_mile), e = parseFloat(edit.end_mile);
    if (!isNaN(s) && !isNaN(e) && e < s) return toast.error("Ending miles must be greater than starting miles");
    try {
      await api.patch(`/cash-miles/${edit.id}`, {
        start_location: edit.start_location || "", end_location: edit.end_location || "",
        start_mile: isNaN(s) ? null : s, end_mile: isNaN(e) ? null : e, date: edit.date,
      });
      toast.success("Trip updated");
      setEdit(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not update trip"); }
  };

  const [report, setReport] = useState(null);
  const [reportYear, setReportYear] = useState(new Date().getFullYear());
  const loadReport = useCallback(async (year) => {
    try { const r = await api.get(`/cash-miles/report?year=${year}`); setReport(r.data); }
    catch { toast.error("Could not build report"); }
  }, []);
  const openReport = () => { const y = new Date().getFullYear(); setReportYear(y); loadReport(y); };
  const changeYear = (y) => { setReportYear(y); loadReport(y); };

  const exportCsv = () => {
    if (!report) return;
    const rows = [["Date", "From", "To", "Start odometer", "End odometer", "Miles", `Deduction (@$${report.rate}/mi)`]];
    report.entries.forEach((e) => rows.push([e.date || "", e.start_location || "", e.end_location || "",
      e.start_mile ?? "", e.end_mile ?? "", e.miles, e.deduction]));
    rows.push([]);
    rows.push(["Monthly summary"]);
    report.months.forEach((m) => rows.push([MONTH_NAMES[m.month - 1], "", "", "", "", m.miles, m.deduction]));
    rows.push(["TOTAL", "", "", "", "", report.total_miles, report.total_deduction]);
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = `mileage-${report.year}.csv`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  };
  const exportPdf = async () => {
    if (!report) return;
    try {
      const { data: blob } = await api.get(`/cash-miles/report.pdf?year=${report.year}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Could not open the PDF"); }
  };

  const logPreset = async (p) => {
    const startStr = window.prompt(`Starting odometer for "${p.label}"?`, lastOdo != null ? String(lastOdo) : "");
    if (startStr === null) return;
    const s = parseFloat(startStr);
    if (isNaN(s)) return toast.error("Enter a valid starting odometer");
    try { await api.post("/cash-miles", { start_location: p.start_location, end_location: p.end_location, start_mile: s, date: today }); toast.success("Trip started — finish it later"); load(); }
    catch { toast.error("Could not log trip"); }
  };
  const savePreset = async () => {
    if (!form.start_location && !form.end_location) return toast.error("Add a start or end location to save a route");
    const label = `${form.start_location || "Trip"} → ${form.end_location || ""}`.trim();
    try { await api.post("/cash-miles/presets", { label, start_location: form.start_location, end_location: form.end_location, miles: miles || 0 }); toast.success("Route saved"); refreshUser(); }
    catch { toast.error("Could not save route"); }
  };
  const delPreset = async (id) => { try { await api.delete(`/cash-miles/presets/${id}`); refreshUser(); } catch {} };

  const inputCls = "mt-2 w-full h-11 px-3 border border-[var(--line)] bg-white text-[var(--text)] placeholder:text-[var(--text-2)] outline-none focus:border-[var(--accent)]";

  return (
    <div data-testid="cash-miles" className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Log a trip */}
        <section className="lg:col-span-5 border border-[var(--line)] bg-white">
          <div className="p-6 border-b border-[var(--line)]">
            <h2 className="font-serif text-2xl tracking-tight flex items-center gap-2"><Car className="w-5 h-5 text-[var(--accent)]" /> Log a trip</h2>
            <p className="text-xs text-[var(--text-2)] mt-1">Log your start now, drive, then come back to finish — deduction auto-calculated at ${data.rate}/mile.</p>
          </div>

          {/* Trips in progress */}
          {openTrips.length > 0 && (
            <div className="p-6 border-b border-[var(--line)] bg-[var(--accent)]/5" data-testid="mile-open-trips">
              <p className="label-mono text-[var(--accent)] mb-3 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Trips in progress — finish when you're back</p>
              <div className="space-y-3">
                {openTrips.map((t) => (
                  <div key={t.id} data-testid={`mile-open-${t.id}`} className="border border-[var(--accent)]/40 rounded-lg p-3 bg-white">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium truncate">{t.start_location || "Trip"} · started at {t.start_mile ?? "—"} mi</p>
                      <span className="text-xs text-[var(--text-2)] font-mono shrink-0">{t.date}</span>
                    </div>
                    <div className="flex items-end gap-2 mt-2">
                      <div className="flex-1">
                        <span className="label-mono text-[var(--text-2)]">Ending miles</span>
                        <input data-testid={`mile-finish-odo-${t.id}`} type="number" step="0.1" value={endEdits[t.id] ?? ""} onChange={(e) => setEndEdits((m) => ({ ...m, [t.id]: e.target.value }))} placeholder="e.g. 45238" className="mt-1 w-full h-10 px-3 border border-[var(--line)] bg-white text-[var(--text)] placeholder:text-[var(--text-2)] outline-none focus:border-[var(--accent)]" />
                      </div>
                      <button data-testid={`mile-finish-${t.id}`} onClick={() => finishTrip(t)} className="h-10 px-3 flex items-center gap-1.5 bg-[var(--accent)] text-white text-sm hover:opacity-90 transition-opacity"><Check className="w-4 h-4" /> Finish</button>
                      <button data-testid={`mile-open-del-${t.id}`} onClick={() => del(t.id)} className="h-10 px-2 flex items-center text-[var(--text-2)] hover:text-[var(--danger)] transition-colors"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="p-6 space-y-4">
            {/* Live GPS tracking */}
            {!tracking ? (
              <button data-testid="mile-live-start" onClick={startTracking} className="w-full flex items-center justify-center gap-2 h-12 bg-[#1A1A18] text-white hover:opacity-90 transition-opacity">
                <Navigation className="w-4 h-4" /> Start Trip — live GPS tracking
              </button>
            ) : (
              <div data-testid="mile-live-card" className="border-2 border-[var(--accent)] rounded-lg p-4 bg-[var(--accent)]/5 space-y-3">
                <div className="flex items-center gap-2">
                  <span className="relative flex h-3 w-3"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[var(--accent)] opacity-75"></span><span className="relative inline-flex rounded-full h-3 w-3 bg-[var(--accent)]"></span></span>
                  <span className="label-mono text-[var(--accent)]">Tracking live…</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><p className="label-mono text-[var(--text-2)]">Distance</p><p data-testid="mile-live-distance" className="font-mono text-2xl font-bold">{liveStats.miles} mi</p></div>
                  <div><p className="label-mono text-[var(--text-2)]">Duration</p><p data-testid="mile-live-duration" className="font-mono text-2xl font-bold">{fmtDur(elapsed)}</p></div>
                </div>
                <div className="flex gap-2">
                  <button data-testid="mile-live-end" onClick={() => stopTracking(true)} className="flex-1 h-11 flex items-center justify-center gap-2 bg-[var(--accent)] text-white hover:opacity-90 transition-opacity"><Square className="w-4 h-4" /> End Trip</button>
                  <button data-testid="mile-live-cancel" onClick={() => stopTracking(false)} className="h-11 px-4 flex items-center justify-center gap-1.5 border border-[var(--line)] text-sm text-[var(--text-2)] hover:border-[var(--danger)] hover:text-[var(--danger)] transition-colors"><X className="w-4 h-4" /> Cancel</button>
                </div>
                <p className="text-xs text-[var(--text-2)]">Keep this tab open. Tracking uses your device GPS only while a trip is active.</p>
              </div>
            )}
            {lastOdo != null && (
              <button data-testid="mile-quick-start" onClick={quickStart} disabled={busy} className="w-full flex items-center justify-between gap-2 border border-[var(--accent)]/40 bg-[var(--accent)]/5 rounded-lg px-4 py-3 text-left hover:bg-[var(--accent)]/10 transition-colors disabled:opacity-50">
                <span className="min-w-0">
                  <span className="label-mono text-[var(--accent)] flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Start from last odometer</span>
                  <span className="block text-xs text-[var(--text-2)] mt-0.5">Begins a new trip at {lastOdo} mi — finish it when you're back.</span>
                </span>
                <span className="shrink-0 h-9 px-3 flex items-center gap-1.5 bg-[var(--accent)] text-white text-sm rounded"><Plus className="w-4 h-4" /> {lastOdo} mi</span>
              </button>
            )}
            {/* Start box */}
            <div className="border border-[var(--line)] rounded-lg p-4 space-y-3">
              <p className="label-mono text-[var(--text-2)] flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-[var(--accent)]" /> Start</p>
              <div>
                <span className="label-mono text-[var(--text-2)]">Starting miles (odometer)</span>
                <div className="flex items-end gap-2">
                  <input data-testid="mile-start-odo" type="number" step="0.1" value={form.start_mile} onChange={(e) => setForm((f) => ({ ...f, start_mile: e.target.value }))} placeholder="e.g. 45210" className={inputCls} />
                  {lastOdo != null && (
                    <button data-testid="mile-autofill" onClick={useLastOdo} className="shrink-0 h-11 px-3 text-xs border border-[var(--line)] text-[var(--text-2)] hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors whitespace-nowrap">Use {lastOdo}</button>
                  )}
                </div>
              </div>
              <div>
                <span className="label-mono text-[var(--text-2)]">Start location <span className="normal-case opacity-70">(optional)</span></span>
                <input data-testid="mile-start" value={form.start_location} onChange={(e) => setForm((f) => ({ ...f, start_location: e.target.value }))} placeholder="Home / office" className={inputCls} />
              </div>
            </div>
            {/* End box */}
            <div className="border border-[var(--line)] rounded-lg p-4 space-y-3">
              <p className="label-mono text-[var(--text-2)] flex items-center gap-1.5"><Flag className="w-3.5 h-3.5 text-[var(--accent)]" /> End <span className="normal-case opacity-70 text-[10px]">(leave blank to finish later)</span></p>
              <div>
                <span className="label-mono text-[var(--text-2)]">Ending miles (odometer)</span>
                <input data-testid="mile-end-odo" type="number" step="0.1" value={form.end_mile} onChange={(e) => setForm((f) => ({ ...f, end_mile: e.target.value }))} placeholder="e.g. 45238" className={inputCls} />
              </div>
              <div>
                <span className="label-mono text-[var(--text-2)]">End location <span className="normal-case opacity-70">(optional)</span></span>
                <input data-testid="mile-end" value={form.end_location} onChange={(e) => setForm((f) => ({ ...f, end_location: e.target.value }))} placeholder="Client site" className={inputCls} />
              </div>
            </div>
            {/* Total + date */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <span className="label-mono text-[var(--text-2)]">Total miles (auto)</span>
                <div data-testid="mile-total-auto" className={`mt-2 w-full h-11 px-3 flex items-center justify-between gap-2 font-mono text-lg font-bold border rounded ${invalidRange ? "border-[var(--danger)] text-[var(--danger)]" : liveMiles != null ? "border-[var(--accent)] text-[var(--accent)] bg-[var(--accent)]/5" : "border-[var(--line)] bg-[var(--bg-2,#f7f7f5)] text-[var(--text)]"}`}>
                  <span>{liveMiles != null ? liveMiles : (miles == null ? "—" : miles)}</span>
                  {liveMiles != null && <span className="text-[10px] font-sans uppercase tracking-wide">GPS</span>}
                </div>
              </div>
              <div>
                <span className="label-mono text-[var(--text-2)]">Date</span>
                <input data-testid="mile-date" type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} className="mt-2 w-full h-11 px-3 border border-[var(--line)] bg-white text-[var(--text)] outline-none focus:border-[var(--accent)]" />
              </div>
            </div>
            {invalidRange && <p className="text-sm text-[var(--danger)]">Ending miles must be greater than starting miles.</p>}
            {(liveMiles != null || (miles != null && miles >= 0)) && (
              <p data-testid="mile-preview" className="text-sm text-[var(--text-2)]">Tax credit for this trip: <span className="font-mono text-[var(--accent)]">{money((liveMiles != null ? liveMiles : miles) * data.rate)}</span>{liveMiles != null && <button data-testid="mile-live-discard" onClick={() => { setLiveMiles(null); setMapRoute(null); }} className="ml-2 text-xs underline hover:text-[var(--accent)]">discard GPS distance</button>}</p>
            )}
            <button data-testid="mile-add" onClick={submit} disabled={busy || tracking} className="w-full h-11 flex items-center justify-center gap-2 bg-[var(--accent)] text-white hover:opacity-90 transition-opacity disabled:opacity-50">
              {liveMiles != null ? <Check className="w-4 h-4" /> : hasEnd ? <Plus className="w-4 h-4" /> : <Clock className="w-4 h-4" />} {busy ? "Saving…" : liveMiles != null ? `Save GPS trip · ${liveMiles} mi` : hasEnd ? "Add trip" : "Start trip — finish later"}
            </button>
            <button data-testid="mile-save-preset" onClick={savePreset} className="w-full h-9 flex items-center justify-center gap-2 border border-[var(--line)] text-sm text-[var(--text-2)] hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors">
              <Bookmark className="w-3.5 h-3.5" /> Save as route preset
            </button>
            {presets.length > 0 && (
              <div className="pt-3 border-t border-[var(--line)]">
                <p className="label-mono text-[var(--text-2)] mb-2 flex items-center gap-1"><Bookmark className="w-3.5 h-3.5" /> Saved routes — tap to start</p>
                <div className="flex flex-wrap gap-2" data-testid="mile-presets">
                  {presets.map((p) => (
                    <div key={p.id} className="flex items-center gap-1 border border-[var(--line)] rounded-full pl-3 pr-1.5 py-1 text-xs bg-white hover:border-[var(--accent)] transition-colors">
                      <button data-testid={`mile-preset-${p.id}`} onClick={() => logPreset(p)} className="flex items-center gap-1"><MapPin className="w-3 h-3 text-[var(--accent)]" /> {p.label} · {p.miles}mi</button>
                      <button data-testid={`mile-preset-del-${p.id}`} onClick={() => delPreset(p.id)} className="text-[var(--text-2)] hover:text-[var(--danger)]"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Route map */}
        <section className="lg:col-span-7 border border-[var(--line)] bg-white flex flex-col" data-testid="mile-map-section">
          <div className="p-6 border-b border-[var(--line)] flex items-center justify-between gap-2">
            <h2 className="font-serif text-2xl tracking-tight flex items-center gap-2"><MapPin className="w-5 h-5 text-[var(--accent)]" /> Route map</h2>
            {mapRoute && <button data-testid="mile-map-clear" onClick={() => { setMapRoute(null); setSelectedId(null); }} className="text-xs text-[var(--text-2)] hover:text-[var(--accent)] transition-colors">Clear</button>}
          </div>
          <div className="flex-1 min-h-[420px]">
            <RouteMap route={mapRoute} loading={mapLoading} live={tracking ? { path: livePath, current: liveCurrent } : null} />
          </div>
        </section>
      </div>

      {/* Totals + list */}
      <section className="border border-[var(--line)] bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 p-6 border-b border-[var(--line)]">
          <h2 className="font-serif text-2xl tracking-tight">Mileage & credit</h2>
          <div className="flex items-center gap-2">
            <button data-testid="mile-report-btn" onClick={openReport} className="flex items-center gap-1.5 h-9 px-3 text-sm border border-[var(--line)] bg-white hover:border-[var(--accent)] transition-colors"><FileBarChart className="w-4 h-4" /> Yearly report</button>
            <div className="flex gap-1 border border-[var(--line)] rounded overflow-hidden" data-testid="mile-period">
            {PERIODS.map((p) => (
              <button key={p.v} data-testid={`mile-period-${p.v}`} onClick={() => setPeriod(p.v)} className={`px-3 h-9 text-sm transition-colors ${period === p.v ? "bg-[#1A1A18] text-white" : "bg-white text-[var(--text-2)] hover:text-[var(--text)]"}`}>{p.l}</button>
            ))}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 p-6 border-b border-[var(--line)]">
          <div className="rounded-lg border border-[var(--line)] p-4">
            <p className="label-mono text-[var(--text-2)]">Total miles</p>
            <p data-testid="mile-total-miles" className="font-mono text-2xl font-bold mt-1">{data.total_miles}</p>
          </div>
          <div className="rounded-lg border border-[var(--accent)]/40 bg-[var(--accent)]/5 p-4">
            <p className="label-mono text-[var(--text-2)] flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5" /> Est. tax credit</p>
            <p data-testid="mile-total-credit" className="font-mono text-2xl font-bold mt-1 text-[var(--accent)]">{money(data.total_deduction)}</p>
          </div>
        </div>
        <div className="divide-y divide-[var(--line)] max-h-[420px] overflow-y-auto scroll-thin">
          {data.entries.length === 0 && <p className="p-8 text-center text-sm text-[var(--text-2)]">No trips logged in this {period}. Add one on the left.</p>}
          {data.entries.map((e) => {
            const open = e.end_mile == null;
            return (
            <div key={e.id} data-testid={`mile-row-${e.id}`} className={`flex items-center gap-3 p-4 group transition-colors ${selectedId === e.id ? "bg-[var(--accent)]/5" : ""}`}>
              <button type="button" data-testid={`mile-select-${e.id}`} onClick={() => selectRoute(e)} className="min-w-0 flex-1 text-left" title="Show route on map">
                <p className="text-sm font-medium flex items-center gap-1.5 truncate"><MapPin className={`w-3.5 h-3.5 shrink-0 ${selectedId === e.id ? "text-[var(--accent)]" : "text-[var(--accent)]"}`} /> {e.start_location || "—"} → {e.end_location || "—"} {open && <span className="text-[10px] font-mono text-[var(--accent)] border border-[var(--accent)]/40 rounded px-1 py-0.5 shrink-0">IN PROGRESS</span>}</p>
                <p className="text-xs text-[var(--text-2)] font-mono mt-0.5">{e.date} · {e.miles} mi{e.start_mile != null ? ` (${e.start_mile}${e.end_mile != null ? `→${e.end_mile}` : "→…"})` : ""}</p>
              </button>
              <span className="font-mono text-sm text-[var(--accent)] shrink-0">{money(e.deduction)}</span>
              <button data-testid={`mile-edit-${e.id}`} onClick={() => setEdit({ id: e.id, start_location: e.start_location || "", end_location: e.end_location || "", start_mile: e.start_mile ?? "", end_mile: e.end_mile ?? "", date: e.date })} className="shrink-0 text-[var(--text-2)] hover:text-[var(--accent)] transition-colors opacity-100 sm:opacity-0 sm:group-hover:opacity-100"><Pencil className="w-4 h-4" /></button>
              <button data-testid={`mile-del-${e.id}`} onClick={() => del(e.id)} className="shrink-0 text-[var(--text-2)] hover:text-[var(--danger)] transition-colors opacity-100 sm:opacity-0 sm:group-hover:opacity-100"><Trash2 className="w-4 h-4" /></button>
            </div>
          );})}
        </div>
        <p className="px-6 py-3 text-xs text-[var(--text-2)] border-t border-[var(--line)]">This {period}'s credit flows into SnapBudget Personal automatically as a mileage credit line.</p>
      </section>

      {/* Edit modal */}
      <Modal open={!!edit} onClose={() => setEdit(null)} testid="mile-edit-modal">
        {edit && (
          <div className="p-6 text-[var(--text)] space-y-4" data-testid="mile-edit">
            <h2 className="font-serif text-2xl">Edit trip</h2>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <span className="label-mono text-[var(--text-2)]">Starting miles</span>
                <input data-testid="mile-edit-start-odo" type="number" step="0.1" value={edit.start_mile} onChange={(e) => setEdit((v) => ({ ...v, start_mile: e.target.value }))} className={inputCls} />
              </div>
              <div>
                <span className="label-mono text-[var(--text-2)]">Ending miles</span>
                <input data-testid="mile-edit-end-odo" type="number" step="0.1" value={edit.end_mile} onChange={(e) => setEdit((v) => ({ ...v, end_mile: e.target.value }))} className={inputCls} />
              </div>
              <div>
                <span className="label-mono text-[var(--text-2)]">Start location</span>
                <input data-testid="mile-edit-start" value={edit.start_location} onChange={(e) => setEdit((v) => ({ ...v, start_location: e.target.value }))} className={inputCls} />
              </div>
              <div>
                <span className="label-mono text-[var(--text-2)]">End location</span>
                <input data-testid="mile-edit-end" value={edit.end_location} onChange={(e) => setEdit((v) => ({ ...v, end_location: e.target.value }))} className={inputCls} />
              </div>
              <div>
                <span className="label-mono text-[var(--text-2)]">Date</span>
                <input data-testid="mile-edit-date" type="date" value={edit.date} onChange={(e) => setEdit((v) => ({ ...v, date: e.target.value }))} className="mt-2 w-full h-11 px-3 border border-[var(--line)] bg-white text-[var(--text)] outline-none focus:border-[var(--accent)]" />
              </div>
              <div className="flex items-end">
                <div className="w-full">
                  <span className="label-mono text-[var(--text-2)]">Total miles</span>
                  <div className="mt-2 w-full h-11 px-3 flex items-center font-mono text-lg font-bold border border-[var(--line)] bg-[var(--bg-2,#f7f7f5)] rounded">{computeMiles(edit.start_mile, edit.end_mile) ?? "—"}</div>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button data-testid="mile-edit-cancel" onClick={() => setEdit(null)} className="h-10 px-4 text-sm border border-[var(--line)] hover:border-[var(--accent)] transition-colors">Cancel</button>
              <button data-testid="mile-edit-save" onClick={saveEdit} className="h-10 px-4 text-sm bg-[var(--accent)] text-white hover:opacity-90 transition-opacity">Save</button>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={!!report} onClose={() => setReport(null)} testid="mile-report-modal">
        {report && (
          <div className="p-6 text-[var(--text)]" data-testid="mile-report">
            <div className="flex flex-wrap items-center justify-between gap-3 print-hide">
              <h2 className="font-serif text-2xl">Mileage Report</h2>
              <div className="flex items-center gap-2">
                <select data-testid="mile-report-year" value={reportYear} onChange={(e) => changeYear(Number(e.target.value))} className="h-9 px-2 text-sm border border-[var(--line)] bg-white text-[var(--text)] outline-none focus:border-[var(--accent)]">
                  {(report.available_years || [reportYear]).map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
                <button data-testid="mile-report-csv" onClick={exportCsv} className="flex items-center gap-1.5 h-9 px-3 text-sm border border-[var(--line)] hover:border-[var(--accent)] transition-colors"><FileText className="w-4 h-4" /> CSV</button>
                <button data-testid="mile-report-pdf" onClick={exportPdf} className="flex items-center gap-1.5 h-9 px-3 text-sm border border-[var(--line)] hover:border-[var(--accent)] transition-colors"><Download className="w-4 h-4" /> PDF</button>
                <button data-testid="mile-report-print" onClick={() => window.print()} className="flex items-center gap-1.5 h-9 px-3 text-sm border border-[var(--line)] hover:border-[var(--accent)] transition-colors"><Printer className="w-4 h-4" /> Print</button>
              </div>
            </div>
            <p className="text-sm text-[var(--text-2)] mt-1">Tax year {report.year} · IRS rate ${report.rate}/mile · {report.count} trip(s) logged</p>
            <table className="w-full mt-4 text-sm">
              <thead><tr className="border-b border-[var(--line)] text-left text-[var(--text-2)]"><th className="py-2">Month</th><th className="py-2 text-right">Miles</th><th className="py-2 text-right">Deduction</th></tr></thead>
              <tbody>
                {report.months.map((m) => (
                  <tr key={m.month} className="border-b border-[var(--line)]/60"><td className="py-1.5">{MONTH_NAMES[m.month - 1]}</td><td className="py-1.5 text-right font-mono">{m.miles}</td><td className="py-1.5 text-right font-mono">{money(m.deduction)}</td></tr>
                ))}
              </tbody>
              <tfoot><tr className="font-bold border-t-2 border-[var(--text)]"><td className="py-2">Total</td><td className="py-2 text-right font-mono" data-testid="mile-report-total-miles">{report.total_miles}</td><td className="py-2 text-right font-mono text-[var(--accent)]" data-testid="mile-report-total-credit">{money(report.total_deduction)}</td></tr></tfoot>
            </table>
          </div>
        )}
      </Modal>
    </div>
  );
}
